import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, X, CalendarClock, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";

const DAY_LABELS = {
  it: ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
};
const WEEKDAY_SHORT = {
  it: ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"],
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function AdminBookings() {
  const { lang } = useLanguage();
  const [enabled, setEnabled] = useState(null);
  const [days, setDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [bookings, setBookings] = useState([]);
  const [agendaMonth, setAgendaMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const byDate = {};
  bookings.forEach((bk) => {
    (byDate[bk.date] = byDate[bk.date] || []).push(bk);
  });
  const dayBookings = selectedDay ? byDate[selectedDay] || [] : [];
  const agendaOffset = (agendaMonth.getDay() + 6) % 7;
  const agendaDays = new Date(agendaMonth.getFullYear(), agendaMonth.getMonth() + 1, 0).getDate();
  const agendaCells = [
    ...Array.from({ length: agendaOffset }, () => null),
    ...Array.from(
      { length: agendaDays },
      (_, i) => new Date(agendaMonth.getFullYear(), agendaMonth.getMonth(), i + 1)
    ),
  ];

  const refreshBookings = () =>
    api.get("/admin/bookings").then((res) => setBookings(res.data)).catch(() => {});

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => {
        setEnabled(res.data.booking_enabled);
        setDays(res.data.booking_days || []);
        setSlots(res.data.booking_slots || []);
      })
      .catch(() => {});
    refreshBookings();
  }, []);

  const save = async (patch, msg) => {
    try {
      const res = await api.put("/admin/settings", patch);
      setEnabled(res.data.booking_enabled);
      setDays(res.data.booking_days || []);
      setSlots(res.data.booking_slots || []);
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleEnabled = () => save({ booking_enabled: !enabled });

  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b);
    save({ booking_days: next });
  };

  const addSlot = () => {
    const v = newSlot.trim();
    if (!TIME_RE.test(v)) {
      toast.error("Formato orario non valido. Usa HH:MM (es. 09:30).");
      return;
    }
    if (slots.includes(v)) return;
    save({ booking_slots: [...slots, v].sort() }, "Orari aggiornati.");
    setNewSlot("");
  };

  const removeSlot = (v) => save({ booking_slots: slots.filter((s) => s !== v) }, "Orari aggiornati.");

  const removeBooking = async (bk) => {
    await api.delete(`/admin/bookings/${bk.id}`).catch(() => {});
    setBookings(bookings.filter((x) => x.id !== bk.id));
    toast.success("Prenotazione eliminata.");
  };

  return (
    <div>
      <div
        data-testid="admin-booking-settings"
        className="mt-8 bg-white border border-slate-200 p-6 sm:p-8"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <CalendarClock className="h-4 w-4 text-brand" /> Prenotazione online / Online booking
            </p>
            <p className="mt-1 text-xs font-light text-slate-500" data-testid="admin-booking-state">
              {enabled === null
                ? "…"
                : enabled
                  ? "Attiva — i pazienti possono prenotare dal sito / Patients can book from the site"
                  : "Disattivata — nessun calendario mostrato ai pazienti / No calendar shown"}
            </p>
          </div>
          <button
            data-testid="admin-booking-toggle"
            onClick={toggleEnabled}
            disabled={enabled === null}
            className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
              enabled
                ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                : "bg-brand text-white hover:bg-brand-dark"
            } disabled:opacity-50`}
          >
            {enabled ? "Disattiva / Disable" : "Attiva / Enable"}
          </button>
        </div>

        <div className="mt-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Giorni disponibili / Available days</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {DISPLAY_ORDER.map((d) => (
              <button
                key={d}
                data-testid={`admin-booking-day-${d}`}
                onClick={() => toggleDay(d)}
                className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 border ${
                  days.includes(d)
                    ? "bg-brand text-white border-brand"
                    : "border-slate-200 text-slate-600 hover:border-brand hover:text-brand"
                }`}
              >
                {DAY_LABELS[lang][d]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Orari disponibili / Available times</p>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {slots.map((s) => (
              <span
                key={s}
                data-testid={`admin-booking-slot-${s.replace(":", "")}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-medium text-brand"
              >
                {s}
                <button
                  data-testid={`admin-booking-slot-remove-${s.replace(":", "")}`}
                  onClick={() => removeSlot(s)}
                  className="text-brand/60 hover:text-red-600"
                  aria-label={`Rimuovi ${s}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <div className="flex items-center gap-2">
              <Input
                data-testid="admin-booking-new-slot"
                value={newSlot}
                onChange={(e) => setNewSlot(e.target.value)}
                placeholder="HH:MM"
                className="w-24 h-9 text-sm"
                onKeyDown={(e) => e.key === "Enter" && addSlot()}
              />
              <Button
                data-testid="admin-booking-add-slot"
                onClick={addSlot}
                size="sm"
                variant="outline"
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" /> Aggiungi
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 p-6 sm:p-8" data-testid="admin-agenda">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Agenda visite / Visit calendar</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              data-testid="admin-agenda-prev"
              onClick={() => setAgendaMonth(new Date(agendaMonth.getFullYear(), agendaMonth.getMonth() - 1, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="w-40 text-center font-serif text-lg capitalize text-slate-900" data-testid="admin-agenda-month">
              {agendaMonth.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { month: "long", year: "numeric" })}
            </span>
            <Button
              variant="outline"
              size="icon"
              data-testid="admin-agenda-next"
              onClick={() => setAgendaMonth(new Date(agendaMonth.getFullYear(), agendaMonth.getMonth() + 1, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest text-slate-400">
          {WEEKDAY_SHORT[lang].map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {agendaCells.map((d, i) =>
            d === null ? (
              <span key={`empty-${i}`} />
            ) : (
              <button
                key={isoOf(d)}
                data-testid={`admin-agenda-day-${isoOf(d)}`}
                onClick={() => setSelectedDay(isoOf(d))}
                className={`relative aspect-square rounded border text-xs transition-colors duration-200 ${
                  selectedDay === isoOf(d)
                    ? "border-brand bg-brand-soft text-brand font-medium"
                    : "border-slate-100 text-slate-600 hover:border-brand/50"
                }`}
              >
                {d.getDate()}
                {(byDate[isoOf(d)] || []).length > 0 && (
                  <span className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-brand" data-testid={`admin-agenda-dot-${isoOf(d)}`} />
                )}
              </button>
            )
          )}
        </div>
        {selectedDay && (
          <div className="mt-6 border-t border-slate-200 pt-5" data-testid="admin-agenda-bookings">
            <p className="font-serif text-lg text-slate-900 capitalize">{selectedDay}</p>
            {dayBookings.map((bk) => (
              <div key={bk.id} className="mt-3 flex items-center justify-between gap-3 text-sm">
                <span className={bk.status === "cancelled" ? "text-slate-400 line-through" : "text-slate-800"}>
                  <strong>{bk.time}</strong> · {bk.name} · {bk.phone}
                  {bk.status === "cancelled" && <em className="ml-2 text-xs">— disdetta / cancelled</em>}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  data-testid={`admin-agenda-delete-${bk.id}`}
                  onClick={() => removeBooking(bk)}
                  className="text-red-600 hover:text-red-700 shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {dayBookings.length === 0 && (
              <p className="mt-2 text-sm font-light text-slate-400">Nessuna visita / No visits</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 space-y-3">
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
          Prenotazioni ricevute / Received bookings ({bookings.length})
        </p>
        {bookings.map((bk) => (
          <div key={bk.id} data-testid="admin-booking-row" className="bg-white border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-serif text-lg text-slate-900">
                  {bk.date} · {bk.time}
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">{bk.name}</p>
                <p className="text-xs font-light text-slate-500">
                  {bk.phone} {bk.email ? `· ${bk.email}` : ""}
                </p>
                {bk.notes && <p className="mt-2 text-sm font-light text-slate-600">{bk.notes}</p>}
              </div>
              <Button
                variant="outline"
                size="icon"
                data-testid={`admin-booking-delete-${bk.id}`}
                onClick={() => removeBooking(bk)}
                className="text-red-600 hover:text-red-700 shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {bookings.length === 0 && (
          <p className="text-sm font-light text-slate-400">Nessuna prenotazione ricevuta / No bookings yet.</p>
        )}
      </div>
    </div>
  );
}
